import type { PermissionKind } from "permissions/_api/permission-kinds-api-service";
import { kindAllows, kindLabel } from "permissions/_views/wording/permission-wording";

/**
 * Which kinds the permissions search and the home page's Kinds card offer, and how typed text finds
 * them (`docs/design/permissions/PERMISSIONS_UI_DESIGN.md`, "Page header, search and back").
 *
 * Only standing kinds: a per-record kind is reached through its record, which the records search
 * brings (#6174). A retired kind is never offered, and a platform-only kind only to a platform
 * administrator, the one reader who can do anything with it.
 */

export const KINDS_SEGMENT = "kinds";

export function kindPermissionsPath(kind: Pick<PermissionKind, "slug">): string {
  return `/permissions/${KINDS_SEGMENT}/${kind.slug}`;
}

export function offeredKinds(kinds: readonly PermissionKind[], mayAdministerPlatform: boolean): PermissionKind[] {
  return kinds.filter((kind) => !kind.perRecord && !kind.retired && (mayAdministerPlatform || !kind.superUserOnly));
}

/** The kinds `offeredKinds` returns, area by area in the server's order of areas. */
export function offeredKindsByArea(
  areas: readonly string[],
  kinds: readonly PermissionKind[],
  mayAdministerPlatform: boolean,
): { area: string; kinds: PermissionKind[] }[] {
  const offered = offeredKinds(kinds, mayAdministerPlatform);
  return areas
    .map((area) => ({ area, kinds: offered.filter((kind) => kind.area === area) }))
    .filter((group) => group.kinds.length > 0);
}

/**
 * The words of some text, as typed text and a kind's own wording are both compared: lower case, a
 * possessive's "'s" dropped so "member's" is the word "member", and every other mark a space, so
 * "sign-in" is the two words of "own sign-in" whichever side the hyphen is on.
 */
function words(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/['’]s\b/gu, "")
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .split(/\s+/)
    .filter(Boolean);
}

/**
 * The offered kinds whose name or "may …" sentence holds every typed word as the start of one of its
 * words, so "sign" finds signing authority (and the sign-in kinds), and "invite" finds both
 * invitation kinds through their sentences. Nothing typed finds nothing: the search lists people and
 * kinds only once there is something to look for.
 */
export function matchingKinds(kinds: readonly PermissionKind[], text: string): PermissionKind[] {
  const typed = words(text);
  if (typed.length === 0) return [];
  return kinds.filter((kind) => {
    const said = words(`${kindLabel(kind)} ${kindAllows(kind)}`);
    return typed.every((word) => said.some((candidate) => candidate.startsWith(word)));
  });
}

/** The kind at `slug`, whatever it is: a link to a retired or per-record kind still names it. */
export function kindBySlug(kinds: readonly PermissionKind[], slug: string | undefined): PermissionKind | undefined {
  return slug ? kinds.find((kind) => kind.slug === slug) : undefined;
}

/**
 * The kinds of one resource type, such as `LegalEntityAccess`. The retired screens' filters named a
 * type, not a kind, so an old address arrives at `/permissions/kinds/{type}`; a type with one kind
 * is that kind, and a type with several (signing authority and member information are both
 * `LegalEntityAccess`) names none of them (#6177).
 */
export function kindsOfResourceType(kinds: readonly PermissionKind[], resourceType: string | undefined): PermissionKind[] {
  return resourceType ? kinds.filter((kind) => kind.resourceType === resourceType) : [];
}
